import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardTitle,
  Field,
  Input,
  Select,
  Table,
  Td,
  Th,
  useToast,
} from "@/core/ui";
import { ApiError } from "@/core/api/client";
import { useBacklog, type BacklogFilters } from "@/core/backlog/api";
import { useSeasons, type Season } from "@/core/seasons/api";
import {
  useAdminSeasons,
  useCreateSeason,
  useDeleteSeason,
  useUpdateSeason,
  type SeasonInput,
} from "./api";

/** Only puzzles that are, or will be, on a leaderboard belong in a season. */
const SEASON_PUZZLES: BacklogFilters = {
  status: ["scheduled", "published", "archived"],
  type: "",
  mine: false,
  q: "",
};

/** ISO instant -> the `datetime-local` value for the same wall-clock time, or "" when unset. */
export function toLocalInput(iso: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/** `datetime-local` value (local wall-clock) -> ISO instant. */
export function fromLocalInput(local: string): string {
  return local ? new Date(local).toISOString() : "";
}

interface FormState {
  name: string;
  starts_at: string;
  ends_at: string;
  scoring: string;
  puzzle_slugs: string[];
}

const EMPTY: FormState = {
  name: "",
  starts_at: "",
  ends_at: "",
  scoring: "sum_of_ranks",
  puzzle_slugs: [],
};

function fromSeason(season: Season): FormState {
  return {
    name: season.name,
    starts_at: toLocalInput(season.starts_at),
    ends_at: toLocalInput(season.ends_at),
    scoring: season.scoring,
    puzzle_slugs: season.puzzle_slugs,
  };
}

function toInput(form: FormState): SeasonInput {
  return {
    name: form.name.trim(),
    starts_at: fromLocalInput(form.starts_at),
    ends_at: fromLocalInput(form.ends_at),
    scoring: form.scoring,
    puzzle_slugs: form.puzzle_slugs,
  };
}

/** Flattens the `errors` map of a validation_error payload into displayable lines. */
function validationMessages(error: unknown): string[] {
  if (!(error instanceof ApiError) || error.code !== "validation_error") return [];
  const errors = error.payload["errors"];
  if (!errors || typeof errors !== "object") return [];
  return Object.entries(errors as Record<string, unknown>).flatMap(([field, value]) =>
    (Array.isArray(value) ? value : [value]).map((message) => `${field}: ${String(message)}`),
  );
}

export function SeasonsAdmin() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const seasons = useAdminSeasons();
  const strategies = useSeasons();
  const puzzles = useBacklog(SEASON_PUZZLES);
  const create = useCreateSeason();
  const update = useUpdateSeason();
  const remove = useDeleteSeason();

  const [editing, setEditing] = useState<Season | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<string[]>([]);

  const date = (value: string) => new Date(value).toLocaleDateString(i18n.language);
  const scoringLabel = (key: string) => t(`seasons.scoring.${key}`, { defaultValue: key });

  const reset = () => {
    setEditing(null);
    setForm(EMPTY);
    setErrors([]);
  };

  const startEditing = (season: Season) => {
    setEditing(season);
    setForm(fromSeason(season));
    setErrors([]);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErrors([]);
    const body = toInput(form);
    const options = {
      onSuccess: (season: Season) => {
        reset();
        toast(t("admin.seasons.saved", { name: season.name }), "success");
      },
      onError: (error: unknown) => {
        const messages = validationMessages(error);
        if (messages.length) setErrors(messages);
        else toast(t("app.error_generic"), "danger");
      },
    };
    if (editing) update.mutate({ id: editing.id, ...body }, options);
    else create.mutate(body, options);
  };

  const togglePuzzle = (slug: string, checked: boolean) =>
    setForm({
      ...form,
      puzzle_slugs: checked
        ? [...form.puzzle_slugs, slug]
        : form.puzzle_slugs.filter((s) => s !== slug),
    });

  const items = seasons.data ?? [];
  const strategyKeys = strategies.data?.strategies ?? [EMPTY.scoring];
  const candidates = puzzles.data?.results ?? [];

  return (
    <div className="bt-admin__stack">
      <Card>
        <CardTitle>{t("admin.seasons.title")}</CardTitle>
        <Alert tone="info">{t("admin.seasons.hint")}</Alert>
        {seasons.isPending ? <p className="bt-admin__hint">{t("app.loading")}</p> : null}
        {seasons.isSuccess && items.length === 0 ? (
          <p className="bt-admin__hint">{t("seasons.empty")}</p>
        ) : null}
        {items.length > 0 ? (
          <Table className="bt-admin__seasons-table">
            <thead>
              <tr>
                <Th>{t("admin.seasons.name")}</Th>
                <Th>{t("admin.seasons.range")}</Th>
                <Th>{t("admin.seasons.scoring")}</Th>
                <Th>{t("admin.seasons.puzzles")}</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {items.map((season) => (
                <tr key={season.id}>
                  <Td>{season.name}</Td>
                  <Td>
                    {t("seasons.range", {
                      from: date(season.starts_at),
                      to: date(season.ends_at),
                    })}
                  </Td>
                  <Td>
                    <Badge tone="info">{scoringLabel(season.scoring)}</Badge>
                  </Td>
                  <Td className="bt-table__cell--numeric">{season.puzzle_slugs.length}</Td>
                  <Td>
                    <div className="bt-admin__row-actions">
                      <Button variant="ghost" size="sm" onClick={() => startEditing(season)}>
                        {t("admin.edit")}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (editing?.id === season.id) reset();
                          remove.mutate(season.id);
                        }}
                      >
                        {t("admin.delete")}
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
      </Card>

      <Card>
        <CardTitle>
          {editing
            ? t("admin.seasons.edit_title", { name: editing.name })
            : t("admin.seasons.create_title")}
        </CardTitle>
        <form onSubmit={submit} aria-label={t("admin.seasons.form")}>
          <Field label={t("admin.seasons.name")} htmlFor="season-name">
            <Input
              id="season-name"
              required
              autoComplete="off"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <div className="bt-admin__season-dates">
            <Field label={t("admin.seasons.starts_at")} htmlFor="season-starts">
              <Input
                id="season-starts"
                type="datetime-local"
                required
                value={form.starts_at}
                onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
              />
            </Field>
            <Field label={t("admin.seasons.ends_at")} htmlFor="season-ends">
              <Input
                id="season-ends"
                type="datetime-local"
                required
                value={form.ends_at}
                onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
              />
            </Field>
          </div>
          <Field
            label={t("admin.seasons.scoring")}
            htmlFor="season-scoring"
            hint={t(`seasons.rule.${form.scoring === "sum_of_ranks" ? "lowest" : "highest"}`, {
              scoring: scoringLabel(form.scoring),
            })}
          >
            <Select
              id="season-scoring"
              value={form.scoring}
              onChange={(e) => setForm({ ...form, scoring: e.target.value })}
            >
              {strategyKeys.map((key) => (
                <option key={key} value={key}>
                  {scoringLabel(key)}
                </option>
              ))}
            </Select>
          </Field>
          <fieldset className="bt-admin__role-fieldset">
            <legend className="bt-label">{t("admin.seasons.puzzles")}</legend>
            {puzzles.isSuccess && candidates.length === 0 ? (
              <p className="bt-field__hint">{t("admin.seasons.no_puzzles")}</p>
            ) : null}
            <ul className="bt-admin__puzzle-picker">
              {candidates.map((puzzle) => (
                <li key={puzzle.slug}>
                  <label className="bt-admin__role-toggle">
                    <input
                      type="checkbox"
                      checked={form.puzzle_slugs.includes(puzzle.slug)}
                      onChange={(e) => togglePuzzle(puzzle.slug, e.target.checked)}
                    />
                    {puzzle.title}
                    <span className="bt-admin__puzzle-slug">{puzzle.slug}</span>
                    <Badge tone="neutral">{t(`backlog.status.${puzzle.status}`)}</Badge>
                  </label>
                </li>
              ))}
            </ul>
            <p className="bt-field__hint">{t("admin.seasons.puzzles_hint")}</p>
          </fieldset>
          {errors.length ? (
            <Alert tone="danger">
              <ul className="bt-admin__error-list">
                {errors.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
          <div className="bt-form__actions">
            <Button type="submit" loading={create.isPending || update.isPending}>
              {editing ? t("admin.save") : t("admin.seasons.create")}
            </Button>
            {editing ? (
              <Button type="button" variant="ghost" onClick={reset}>
                {t("admin.cancel")}
              </Button>
            ) : null}
          </div>
        </form>
      </Card>
    </div>
  );
}
